import { useState } from "react";
import toast from "react-hot-toast";
import {
  AnalyzeReviewResponse,
  GenerateResponseResponse,
  Review,
} from "../types";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

export const useReviewResponse = () => {
  const [generatingResponses, setGeneratingResponses] = useState<{
    [key: string]: boolean;
  }>({});
  const { user } = useAuth();

  const handleGenerateResponse = async (
    reviewId: string,
    setReviews: React.Dispatch<React.SetStateAction<Review[]>>,
    platform?: "ozon" | "wildberries" | "yandex",
    requirements?: string
  ) => {
    setGeneratingResponses((prev) => ({ ...prev, [reviewId]: true }));
    try {
      const token = localStorage.getItem("token");

      if (!platform) throw new Error("Не удалось определить платформу отзыва");

      // Если отзыв не проанализирован, сначала анализируем его
      // Всегда гарантируем анализ на стороне внешнего AI-эндпоинта
      if (true) {
        const analyzeResponse = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_ANALYZE),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ platform, externalReviewId: reviewId }),
          }
        );

        if (!analyzeResponse.ok) {
          throw new Error("Ошибка при анализе отзыва");
        }

        // Обновляем отзыв после анализа - объединяем только поля анализа
        const analyzedReview: AnalyzeReviewResponse & {
          isAnalyzed: boolean;
          isResponded: boolean;
          isFallback: boolean;
        } = await analyzeResponse.json();
        setReviews((prev: Review[]) =>
          prev.map((r) => {
            if (r.id === reviewId) {
              return {
                ...r,
                // Обновляем только поля анализа
                sentiment: analyzedReview.sentiment as any,
                sentimentAnalysis: analyzedReview.sentimentAnalysis,
                problems: analyzedReview.problems,
                recommendations: analyzedReview.recommendations,
                isAnalyzed: analyzedReview.isAnalyzed,
                isResponded: analyzedReview.isResponded,
                isFallback: analyzedReview.isFallback,
              };
            }
            return r;
          })
        );
      }

      // Теперь генерируем ответ
      const response = await fetch(
        getApiUrl(API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_RESPONSE),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            platform,
            externalReviewId: reviewId,
            requirements,
            commentOnGeneration:
              requirements && requirements.trim()
                ? undefined
                : (user as any)?.globalPromptNote &&
                    String((user as any).globalPromptNote).trim()
                  ? String((user as any).globalPromptNote).trim()
                  : undefined,
            storeName:
              user?.companyName && user.companyName.trim()
                ? user.companyName.trim()
                : undefined,
          }),
        }
      );

      if (response.ok) {
        const updatedReview: GenerateResponseResponse & {
          isResponded: boolean;
          responseTemplate: string;
        } = await response.json();
        setReviews((prev: Review[]) =>
          prev.map((review) => {
            if (review.id === reviewId) {
              // Объединяем только поля ответа, сохраняя остальные поля оригинального отзыва
              return {
                ...review,
                // Обновляем только поля ответа
                responseTemplate: updatedReview.responseTemplate,
                isResponded: updatedReview.isResponded,
              };
            }
            return review;
          })
        );
        toast.success("Ответ сгенерирован!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Ошибка при генерации ответа:",
          response.status,
          errorData
        );
        toast.error(errorData.message || "Ошибка при генерации ответа");
      }
    } catch (error) {
      console.error("Ошибка при генерации ответа:", error);
      toast.error("Ошибка при генерации ответа");
    } finally {
      setGeneratingResponses((prev) => ({ ...prev, [reviewId]: false }));
    }
  };

  const handleRegenerateResponse = async (
    reviewId: string,
    setReviews: React.Dispatch<React.SetStateAction<Review[]>>,
    platform?: "ozon" | "wildberries" | "yandex",
    requirements?: string
  ) => {
    setGeneratingResponses((prev) => ({ ...prev, [reviewId]: true }));
    try {
      const token = localStorage.getItem("token");

      if (!platform) throw new Error("Не удалось определить платформу отзыва");

      // Если отзыв не проанализирован, сначала анализируем его
      if (true) {
        const analyzeResponse = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_ANALYZE),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ platform, externalReviewId: reviewId }),
          }
        );

        if (!analyzeResponse.ok) {
          throw new Error("Ошибка при анализе отзыва");
        }

        // Обновляем отзыв после анализа - объединяем только поля анализа
        const analyzedReview: AnalyzeReviewResponse & {
          isAnalyzed: boolean;
          isResponded: boolean;
          isFallback: boolean;
        } = await analyzeResponse.json();
        setReviews((prev: Review[]) =>
          prev.map((r) => {
            if (r.id === reviewId) {
              return {
                ...r,
                // Обновляем только поля анализа
                sentiment: analyzedReview.sentiment as any,
                sentimentAnalysis: analyzedReview.sentimentAnalysis,
                problems: analyzedReview.problems,
                recommendations: analyzedReview.recommendations,
                isAnalyzed: analyzedReview.isAnalyzed,
                isResponded: analyzedReview.isResponded,
                isFallback: analyzedReview.isFallback,
              };
            }
            return r;
          })
        );
      }

      // Теперь генерируем новый ответ
      const response = await fetch(
        getApiUrl(API_CONFIG.ENDPOINTS.EXTERNAL_REVIEW_AI_RESPONSE),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            platform,
            externalReviewId: reviewId,
            requirements,
            commentOnGeneration:
              requirements && requirements.trim()
                ? undefined
                : (user as any)?.globalPromptNote &&
                    String((user as any).globalPromptNote).trim()
                  ? String((user as any).globalPromptNote).trim()
                  : undefined,
            storeName:
              user?.companyName && user.companyName.trim()
                ? user.companyName.trim()
                : undefined,
          }),
        }
      );

      if (response.ok) {
        const updatedReview: GenerateResponseResponse & {
          isResponded: boolean;
        } = await response.json();
        setReviews((prev: Review[]) =>
          prev.map((review) => {
            if (review.id === reviewId) {
              // Объединяем только поля ответа, сохраняя остальные поля оригинального отзыва
              return {
                ...review,
                // Обновляем только поля ответа
                responseTemplate: updatedReview.responseTemplate,
                isResponded: updatedReview.isResponded,
              };
            }
            return review;
          })
        );
        toast.success("Ответ перегенерирован!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Ошибка при перегенерации ответа:",
          response.status,
          errorData
        );
        toast.error(errorData.message || "Ошибка при перегенерации ответа");
      }
    } catch (error) {
      console.error("Ошибка при перегенерации ответа:", error);
      toast.error("Ошибка при перегенерации ответа");
    } finally {
      setGeneratingResponses((prev) => ({ ...prev, [reviewId]: false }));
    }
  };

  return {
    generatingResponses,
    handleGenerateResponse,
    handleRegenerateResponse,
  };
};
